/**
 * The example studio config — the one source of it: `acp studio-template`
 * prints it and the SPA's "create from template" offers it. An example config (PLAN §7.1a, D22) in the
 * engine's schema (`@acp/engine/studioConfig.js`) for the three simulated
 * devices (PLAN §7.3) — a vision mixer `vmx1`, a video server `vs1`, graphics
 * `gfx1` — with mappings, layers, the piece types camera, vt, audio,
 * lower-third and fullscreen, and a break action.
 *
 * Device types, mapping options (0-based `me`/`keyer`/`aux`, `channel`,
 * `layer`; `oneShot` true for video-server channels) and the timeline
 * content's `deviceType` are the simulated devices' names (wave 3a-B). Layer
 * ids are timeline layers, so `[A-Za-z0-9_]` only. The simulators listen on
 * the checkout's port block (`dev/sessionPorts.ts`: base + 5, 6, 7);
 * `--base-port` names the block. Pure data, no I/O: it lives in the engine
 * beside the schema it must satisfy (`studioTemplate.test.ts`).
 */
import type { JsonValue } from '@acp/domain/primitives.js';

/** `dev/sessionPorts.ts`'s offsets of the simulated devices in a checkout's port block. */
export const SIM_PORT_OFFSETS = { mixer: 5, video: 6, graphics: 7 } as const;

export function studioTemplate(host: string, basePort: number): { [key: string]: JsonValue } {
  // The simulated devices' timeline content (packages/devices/src/sim): a mixer M/E cut, a clip, a template.
  const mixer = { deviceType: 'ACP_SIM_MIXER', type: 'me' };
  const video = { deviceType: 'ACP_SIM_VIDEO', type: 'clip' };
  const graphics = { deviceType: 'ACP_SIM_GRAPHICS', type: 'template' };
  const text = (label: string, required: boolean) => ({ type: 'string', label, required });
  return {
    devices: {
      vmx1: { type: 'sim-vision-mixer', standbyPolicy: 'observe', options: { host, port: basePort + SIM_PORT_OFFSETS.mixer } },
      vs1: { type: 'sim-video-server', standbyPolicy: 'observe', options: { host, port: basePort + SIM_PORT_OFFSETS.video } },
      gfx1: { type: 'sim-graphics', standbyPolicy: 'observe', options: { host, port: basePort + SIM_PORT_OFFSETS.graphics } },
    },
    mappings: {
      vmx_pgm: { device: 'vmx1', lookahead: 'preload', oneShot: false, options: { mappingType: 'me', me: 0 } },
      vmx_dsk: { device: 'vmx1', lookahead: 'none', oneShot: false, options: { mappingType: 'keyer', me: 0, keyer: 0 } },
      vs_a: { device: 'vs1', lookahead: 'preload', oneShot: true, options: { mappingType: 'channel', channel: 0 } },
      vs_audio: { device: 'vs1', lookahead: 'none', oneShot: true, options: { mappingType: 'channel', channel: 1 } },
      gfx_lt: { device: 'gfx1', lookahead: 'none', oneShot: false, options: { mappingType: 'layer', layer: 0 } },
      gfx_full: { device: 'gfx1', lookahead: 'none', oneShot: false, options: { mappingType: 'layer', layer: 1 } },
    },
    sourceLayers: {
      cam: { label: 'Camera' },
      vt: { label: 'VT' },
      audio: { label: 'Audio' },
      lt: { label: 'Lower third' },
      full: { label: 'Fullscreen graphics' },
    },
    outputLayers: {
      pgm: { label: 'Programme', isPgm: true },
      overlay: { label: 'Overlay', isPgm: false },
    },
    pieceTypes: {
      camera: {
        label: 'Camera',
        params: { input: { type: 'integer', label: 'Mixer input', required: true, minimum: 1, maximum: 8 } },
        sourceLayer: 'cam',
        outputLayer: 'pgm',
        lifespan: 'part-only',
        defaultDuration: null,
        timeline: [{ layer: 'vmx_pgm', content: { ...mixer, input: '{{input}}' } }],
      },
      vt: {
        label: 'VT',
        params: { clip: text('Clip', true) },
        sourceLayer: 'vt',
        outputLayer: 'pgm',
        lifespan: 'part-only',
        defaultDuration: null,
        prerollDuration: 200,
        timeline: [
          { layer: 'vs_a', content: { ...video, clip: '{{clip}}' } },
          { layer: 'vmx_pgm', content: { ...mixer, input: 3 } },
        ],
      },
      audio: {
        label: 'Audio',
        params: { file: text('File', true) },
        sourceLayer: 'audio',
        outputLayer: 'pgm',
        lifespan: 'segment-end',
        defaultDuration: null,
        timeline: [{ layer: 'vs_audio', content: { ...video, clip: '{{file}}', loop: true } }],
      },
      'lower-third': {
        label: 'Lower third',
        params: { name: text('Name', true), title: text('Title', false) },
        sourceLayer: 'lt',
        outputLayer: 'overlay',
        lifespan: 'part-only',
        defaultDuration: 6000,
        timeline: [{ layer: 'gfx_lt', content: { ...graphics, template: 'lower-third', data: { name: '{{name}}', title: '{{title}}' } } }],
      },
      fullscreen: {
        label: 'Fullscreen',
        params: { template: text('Template', true) },
        sourceLayer: 'full',
        outputLayer: 'pgm',
        lifespan: 'part-only',
        defaultDuration: null,
        timeline: [
          { layer: 'gfx_full', content: { ...graphics, template: '{{template}}', data: {} } },
          { layer: 'vmx_pgm', content: { ...mixer, input: 4 } },
        ],
      },
    },
    actions: {
      break: { label: 'Break', steps: [{ stop: 'lt' }, { stop: 'full' }, { pieceType: 'vt', params: { clip: 'break' }, mode: 'now' }] },
    },
    adlibPanels: [
      {
        id: 'main',
        label: 'Main',
        items: [
          { label: 'Cam 1', pieceType: 'camera', params: { input: 1 }, hotkey: 'F1' },
          { label: 'Cam 2', pieceType: 'camera', params: { input: 2 }, hotkey: 'F2' },
          { label: 'Lower third', pieceType: 'lower-third', params: { name: 'Name', title: 'Title' } },
          { label: 'Bumper', pieceType: 'vt', params: { clip: 'bumper' }, mode: 'queue' },
          { label: 'Break', action: 'break' },
        ],
      },
    ],
    settings: { minimumTakeSpanMs: 1000 },
  };
}
